// Browser compatible version of crypto utils

const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const ITERATIONS = 310000;
const KEY_LENGTH = 256;
const ALGORITHM = "AES-GCM";

export class PasscodeError extends Error {
	constructor(message = "Invalid passcode") {
		super(message);
		this.name = "PasscodeError";
	}
}

export class PasscodeCrypto {
	private static async deriveKey(
		passcode: string,
		salt: Uint8Array,
	): Promise<CryptoKey> {
		const encoder = new TextEncoder();
		const keyMaterial = await crypto.subtle.importKey(
			"raw",
			encoder.encode(passcode),
			"PBKDF2",
			false,
			["deriveKey"],
		);

		return crypto.subtle.deriveKey(
			{
				name: "PBKDF2",
				salt: salt as unknown as ArrayBuffer,
				iterations: ITERATIONS,
				hash: "SHA-256",
			},
			keyMaterial,
			{ name: ALGORITHM, length: KEY_LENGTH },
			false,
			["encrypt", "decrypt"],
		);
	}

	static async encrypt(data: any, passcode: string): Promise<string> {
		const encoder = new TextEncoder();

		const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
		const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
		const key = await PasscodeCrypto.deriveKey(passcode, salt);
		const encodedData = encoder.encode(JSON.stringify(data));

		const encryptedContent = await crypto.subtle.encrypt(
			{ name: ALGORITHM, iv },
			key,
			encodedData,
		);
		const encryptedBuffer = new Uint8Array(
			salt.length + iv.length + encryptedContent.byteLength,
		);

		encryptedBuffer.set(salt, 0);
		encryptedBuffer.set(iv, salt.length);
		encryptedBuffer.set(
			new Uint8Array(encryptedContent),
			salt.length + iv.length,
		);

		return bytesToBase64(encryptedBuffer);
	}

	static async decrypt<T>(
		encryptedData: string,
		passcode: string,
	): Promise<{ ok: true; data: T } | { ok: false; error: Error }> {
		try {
			const decoder = new TextDecoder();
			const encryptedArray = base64ToBytes(encryptedData);
			const salt = encryptedArray.subarray(0, SALT_LENGTH);
			const iv = encryptedArray.subarray(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
			const content = encryptedArray.subarray(SALT_LENGTH + IV_LENGTH);

			const key = await PasscodeCrypto.deriveKey(passcode, salt);
			const decryptedContent = await crypto.subtle.decrypt(
				{ name: ALGORITHM, iv },
				key,
				content,
			);
			return { ok: true, data: JSON.parse(decoder.decode(decryptedContent)) };
		} catch (e) {
			if (e instanceof Error && e.name === "OperationError") {
				return { ok: false, error: new PasscodeError("Invalid passcode") };
			}

			return {
				ok: false,
				error: e instanceof Error ? e : new Error("Unknown error"),
			};
		}
	}
}

function bytesToBase64(bytes: Uint8Array) {
	const chunkSize = 1024;
	const chunks: string[] = [];

	for (let i = 0; i < bytes.length; i += chunkSize) {
		const chunk = bytes.subarray(i, i + chunkSize);
		let binary = "";

		for (let j = 0; j < chunk.length; j++) {
			binary += String.fromCharCode(chunk[j]);
		}

		chunks.push(binary);
	}

	return btoa(chunks.join(""));
}

function base64ToBytes(base64: string) {
	const binaryLength = Math.floor((base64.length * 3) / 4);
	const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
	const bytes = new Uint8Array(binaryLength - padding);

	const chunkSize = 1024;
	let byteOffset = 0;

	for (let i = 0; i < base64.length; i += chunkSize) {
		const chunk = base64.slice(i, i + chunkSize);
		const binary = atob(chunk);

		for (let j = 0; j < binary.length; j++) {
			bytes[byteOffset++] = binary.charCodeAt(j);
		}
	}

	return bytes;
}
